/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { DataTable, DataTableRowToggleEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import CalendarDateSelector from "../CalendarDateSelector";
import { convertUTCEpochToDate } from "../../../../helper/DateConverters";
import { tableHeaderTemplate } from "../../Work Time Log Management/common/TimeLogTemplates";
import { fetchAllEnvironmentalSurveyingChecklistForm } from "./fetchData";

const EnvironmentalSurveyingListing: React.FC = () => {
  const [expandedRows, setExpandedRows] = useState<any>([]);
  const [environmentalSurveyingChecklist, setEnvironmentalSurveyingChecklist] =
    useState<any>([]);
  const [
    environmentalSurveyingChecklistLoading,
    setEnvironmentalSurveyingChecklistLoading,
  ] = useState<boolean>(false);

  const [filters, setFilters] = useState<any>({
    startDate: null,
    endDate: null,
  });
  const toast = useRef<Toast>(null);

  const [sortFilter, setSortFilter] = useState<any>({
    sortField: "formDate",
    sortOrder: true,
  });

  const navigation = useNavigate();

  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      fetchAllEnvironmentalSurveyingChecklistForm(
        setEnvironmentalSurveyingChecklist,
        setEnvironmentalSurveyingChecklistLoading,
        filters,
        toast
      );
    }
  }, [filters?.startDate, filters.endDate]);

  useEffect(() => {
    if (environmentalSurveyingChecklist.length > 0) {
      setExpandedRows([environmentalSurveyingChecklist[0]]); // Set the first row as expanded
    }
  }, [environmentalSurveyingChecklist]);

  const environmentalSurveyingListLocationBodyTemplate = (rowData: any) => {
    return (
      <div className="tw-flex tw-items-center tw-gap-2 hover:tw-bg-[#E8F9FF] tw-p-2 tw-cursor-pointer">
        <div className="tw-text-gray-700 tw-text-base tw-font-semibold tw-cursor-pointer">
          {rowData?.formJson?.location || "-"}
        </div>
      </div>
    );
  };

  const teamMembersBodyTemplate = (rowData: any) => {
    const teamMembers = [];

    if (rowData.formJson.teamMember1) {
      teamMembers.push(rowData.formJson.teamMember1.displayName);
    }

    if (rowData.formJson.teamMember2) {
      teamMembers.push(rowData.formJson.teamMember2.displayName);
    }

    if (
      rowData.formJson.additionalTeamMembers &&
      Array.isArray(rowData.formJson.additionalTeamMembers)
    ) {
      rowData.formJson.additionalTeamMembers.forEach((member: any) => {
        teamMembers.push(member.displayName);
      });
    }

    return (
      <div className="tw-text-gray-700 tw-text-sm tw-font-medium">
        {teamMembers.filter(Boolean).join(", ")}
      </div>
    );
  };

  const navigate = useNavigate();

  // Custom error template (can also include icons or custom components)
  const emptyMessageTemplate = (
    <div style={{ textAlign: "center", fontSize: "1.25rem" }}>
      <h4>No records found.</h4>
    </div>
  );

  return (
    <>
      <Toast ref={toast} />
      <div className=" tw-bg-[#F8FAFC] tw-border-solid tw-border tw-border-[#E2E8F0] tw-rounded-lg">
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-4 tw-p-4 tw-gap-2 tw-flex-wrap">
          <h2 className="tw-text-xl tw-font-semibold">
            Environment Surveying Listing
          </h2>
          <div className="tw-flex tw-gap-2 tw-items-center tw-justify-end tw-flex-wrap">
            <CalendarDateSelector setDateRange={setFilters} />
            <Button
              className="tw-flex tw-px-3 tw-py-2 tw-rounded bg-primary border-primary tw-text-white tw-font-medium"
              label="Add"
              icon="pi pi-plus"
              onClick={() =>
                navigate(`/layout/health-and-safety/add-environment-surveying`)
              }
            />
          </div>
        </div>
        {environmentalSurveyingChecklistLoading ? (
          <div className="tw-h-full tw-w-full tw-bg-transparent tw-flex tw-justify-center tw-items-center tw-absolute tw-top-0 tw-left-0 tw-z-50">
            <ProgressSpinner />
          </div>
        ) : (
          <DataTable
            value={environmentalSurveyingChecklist}
            emptyMessage={emptyMessageTemplate}
            rowGroupMode="subheader"
            groupRowsBy="formDate"
            expandableRowGroups
            expandedRows={expandedRows}
            onRowToggle={(e: DataTableRowToggleEvent) => {
              setExpandedRows(e.data);
            }}
            tableStyle={{ minWidth: "50rem" }}
            rowGroupHeaderTemplate={(data: any) => {
              return (
                <div className="tw-inline-flex tw-justify-between tw-items-center tw-gap-3 table-row-group-header tw-bg-[#F8FAFC] tw-pl-2 tw-tex tw-font-semibold tw-text-base tw-text-[#64748B]">
                  {convertUTCEpochToDate(data.formDate)}
                </div>
              );
            }}
            onRowClick={(e) => {
              const rowData = e.data;
              navigation(
                `/layout/health-and-safety/view-environmental-surveying?formID=${rowData.id}`
              );
            }}
            rowClassName={() =>
              "hover:tw-bg-[#E8F9FF] tw-cursor-pointer tw-text-[#64748B]"
            }
            className="a-table timelog-datatable tw-text-nowrap tw-text-sm tw-text-[#64748B]"
            style={{ backgroundColor: "#000 !important" }}
          >
            <Column
              field="project"
              header={tableHeaderTemplate("Project", "", setSortFilter)}
              body={(rowData) => {
                return (
                  <div className="tw-text-gray-700 tw-text-sm tw-font-medium">
                    {rowData?.formJson?.projectKeyName || ""}
                  </div>
                );
              }}
            />
            <Column
              field="teamMembers"
              header={tableHeaderTemplate("Team Members", "", setSortFilter)}
              body={teamMembersBodyTemplate}
            />
            <Column
              field="location"
              header={tableHeaderTemplate("Location", "", setSortFilter)}
              body={environmentalSurveyingListLocationBodyTemplate}
            />
            <Column
              field="Created On"
              header={tableHeaderTemplate("Created On", "", setSortFilter)}
              body={(rowData) => {
                return (
                  <div className="tw-text-gray-700 tw-text-sm tw-font-medium">
                    <span className="">
                      {new Date(rowData.createdDate).toLocaleTimeString(
                        "en-EN",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        }
                      )}
                    </span>
                  </div>
                );
              }}
            />
          </DataTable>
        )}
      </div>
    </>
  );
};

export default EnvironmentalSurveyingListing;
